import type { SubmissionResults } from "../../services/calculatorService";

/**
 * Each assessment stores its own `result_payload` key names, while the results
 * panel renders a single flat key set (`cost`, `waste`, `emissions`, ...). This
 * translates between the two.
 *
 * Lives outside Calculator.tsx so it can be tested directly: a backend rename
 * turns into an empty panel rather than an error, so the key names are pinned by
 * `procurementResults.test.ts` against real recorded payloads.
 *
 * Note this is only one of the two routes into the panel. Submitting sets the
 * response directly, and `CalculatorService.submitCalculator` normalises that
 * one; this is the path taken when a locked assessment is reloaded.
 */

const toNumber = (value: unknown): number | undefined => {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
};

export const mapAssessmentPayloadToSubmissionResults = (
  category: string,
  result: Record<string, unknown> | undefined,
): SubmissionResults => {
  if (!result) return {};

  if (category === "Lab Waste") {
    return {
      waste: toNumber(result.waste_base_kg),
      cost: toNumber(result.cost_base),
      emissions: toNumber(result.emissions_base),
    };
  }

  if (category === "Cold Storage" || category === "Computer Hardware") {
    return {
      energy: toNumber(result.annual_energy_kwh),
      cost: toNumber(result.annual_cost),
      emissions: toNumber(result.annual_emissions),
    };
  }

  if (category === "Transport") {
    return {
      energy_mj: toNumber(result.total_energy_mj) ?? toNumber(result.energy_mj),
      emissions_kgco2e:
        toNumber(result.total_emissions_kgco2e) ??
        toNumber(result.emissions_kgco2e),
    };
  }

  if (category === "Green Chemistry") {
    return {
      volume_ml:
        toNumber(result.total_annual_volume_ml) ?? toNumber(result.volume_ml),
      cost:
        toNumber(result.total_annual_cost_aud) ??
        toNumber(result.cost_aud) ??
        toNumber(result.cost),
      emissions:
        toNumber(result.total_annual_emissions_kgco2e) ??
        toNumber(result.emissions_kgco2e) ??
        toNumber(result.emissions),
      toxicity_burden:
        toNumber(result.total_annual_toxicity_burden) ??
        toNumber(result.toxicity_burden),
    };
  }

  if (category === "Procurement") {
    // The payload carries baseline, adjusted and avoided figures for each of
    // cost, waste and emissions. Only the baseline set is mapped, matching what
    // Lab Waste does with its `*_base` keys.
    //
    // This mapper is not mode-aware: savings reaches it through getDeltaResult
    // with the same category string. Locking a baseline does create a savings
    // submission (`procurement.py` createNextSubmissionTypes), so once the delta
    // payload's shape is confirmed, savings should map the `*_avoided` and
    // `annual_cost_savings` figures here instead.
    return {
      units: toNumber(result.annual_units_procured),
      waste: toNumber(result.baseline_annual_waste_kg),
      cost: toNumber(result.baseline_annual_cost),
      emissions: toNumber(result.baseline_annual_emissions_kgco2e),
    };
  }

  return {};
};
